// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.
// src/pages/AnalyzeImage.js
import { useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import Paywall from '../components/Paywall';
import { useSubscription } from '../components/SubscriptionContext';
import './Services.css';

const aiFeatures = [
  'Image analysis with Azure CV',
  'Face detection and attributes',
  'Speech ↔ Text conversions',
  'Single ₹5 unlock for all AI services',
];

const AnalyzeImage = () => {
  const { user } = useAuth0();
  const email = user?.email;
  const { isSubscribedForEmail, isFeatureUnlocked, subscribeForFeature } = useSubscription();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSubscribed = isSubscribedForEmail(email) || isFeatureUnlocked('ai');

  if (!isSubscribed) {
    return (
      <div className="service-page">
        <Paywall
          title="Unlock AI Services"
          description="Subscribe for ₹5 to run image analysis, face detection, and speech tools."
          onSubscribe={() => subscribeForFeature('ai')}
          ctaLabel="Subscribe for ₹5"
          priceLabel="₹5"
          features={aiFeatures}
        />
      </div>
    );
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please choose an image first.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await axios.post(
        'https://my-computervision-api.cognitiveservices.azure.com/vision/v3.2/analyze?visualFeatures=Categories,Description,Color',
        new Blob([file]),
        {
          headers: {
            'Content-Type': 'application/octet-stream',
            'Ocp-Apim-Subscription-Key': process.env.REACT_APP_CV_KEY
          }
        }
      );
      setResult(response.data);
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Unable to analyze image right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const dominantColors = result?.color?.dominantColors || [];
  const categories = result?.categories || [];
  const tags = result?.description?.tags || [];
  const captions = result?.description?.captions || [];
  const meta = result?.metadata || {};

  return (
    <div className="service-page ai-analyze">
      <div className="service-heading">
        <div>
          <h1>Analyze Image</h1>
          <p className="service-description">Run Azure Computer Vision to detect categories, colors, and captions from your image.</p>
        </div>
        <div className="service-actions">
          <span className="action-chip">AI Services</span>
          <span className="action-chip">Azure CV</span>
        </div>
      </div>

      <div className="service-grid">
        <div className="service-form">
          <label>
            Upload image
            <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} />
          </label>
          <button className="primary-btn" onClick={handleUpload} disabled={loading}> {loading ? 'Analyzing…' : 'Analyze Image'} </button>
          {error && (
            <div className="status-banner error">
              <span className="status-icon error">!</span>
              <span>{error}</span>
            </div>
          )}
          {loading && (
            <div className="status-banner">
              <span className="status-icon check">★</span>
              <span>Thinking… running analysis</span>
            </div>
          )}
        </div>

        {result && (
          <div className="analysis-grid">
            <div className="analysis-card highlight">
              <p className="analysis-label">Caption</p>
              <h3>{captions[0]?.text || 'No caption detected'}</h3>
              <p className="analysis-sub">Confidence: {(captions[0]?.confidence * 100 || 0).toFixed(1)}%</p>
            </div>

            <div className="analysis-card">
              <p className="analysis-label">Tags</p>
              <div className="chip-row">
                {tags.length ? tags.map((tag) => (<span key={tag} className="pill">{tag}</span>)) : <span className="pill muted">No tags</span>}
              </div>
            </div>

            <div className="analysis-card">
              <p className="analysis-label">Colors</p>
              <div className="color-blocks">
                <div className="color-chip">
                  <span className="swatch" style={{ background: `#${result?.color?.accentColor || 'fff'}` }}></span>
                  <span>Accent #{result?.color?.accentColor}</span>
                </div>
                {dominantColors.map((c) => (
                  <div key={c} className="color-chip">
                    <span className="swatch" style={{ background: c.toLowerCase() }}></span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="analysis-card">
              <p className="analysis-label">Categories</p>
              <ul className="analysis-list">
                {categories.map((c) => (
                  <li key={c.name}>
                    <span>{c.name.replace('animal_', '').replace('_', ' ')}</span>
                    <span className="score">{(c.score * 100).toFixed(1)}%</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="analysis-card">
              <p className="analysis-label">Metadata</p>
              <div className="meta-grid">
                <div><span className="meta-label">Width</span><span className="meta-value">{meta.width}px</span></div>
                <div><span className="meta-label">Height</span><span className="meta-value">{meta.height}px</span></div>
                <div><span className="meta-label">Format</span><span className="meta-value">{meta.format}</span></div>
                <div><span className="meta-label">Model</span><span className="meta-value">{result?.modelVersion}</span></div>
                <div><span className="meta-label">Request</span><span className="meta-value">{result?.requestId}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {result && (
        <div className="service-result raw">
          <p className="analysis-label">Raw response</p>
          <pre>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default AnalyzeImage;